import React, { useMemo } from 'react'

import { LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { MultipleValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule } from '@shared/table/TableIndex'
import { Tag, Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { formatDateTime } from '../../../utils/dateTime'

interface UnknownProductsListProps {
  role?: 'admin' | 'operator';
  service: TableService;
  showMerchantColumn?: boolean;
  lastModified?: number;
  setLastModified?: (value: number) => void;
}

interface UnknownProductRow {
  id: string;
  display_identifier?: string;
  sku?: string;
  name?: string;
  pending_orders_count?: number;
  total_quantity?: number;
  created_at?: string;
  merchant?: { id: string; name?: string } | null;
  ec_account_name?: string | null;
  platform?: string | null;
  [key: string]: unknown;
}

export default function UnknownProductsList({
  role = 'admin',
  service, // New
  showMerchantColumn = false,
  lastModified,
  setLastModified
}: UnknownProductsListProps) {
  const { t } = useTranslation()

  const columns = useMemo(() => {
    const cols: any[] = [
      {
        nameKey: 'models.unknown_product.display_identifier',
        persist: true,
        sortable: true,
        sortFieldName: 'unknown_products.name',
        width: '250px',
        wrap: true,
        cell: ({ id, display_identifier }: UnknownProductRow) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/unknown_products/${id}`} style={{ wordBreak: 'break-word' }}>
              {display_identifier}
            </Link>
            <a
              href={`/${role}/unknown_products/${id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ marginLeft: '8px', flexShrink: 0 }}
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        )
      },
      {
        nameKey: 'models.unknown_product.sku',
        persist: true,
        sortable: true,
        sortFieldName: 'unknown_products.sku',
        width: '150px',
        cell: ({ sku }: UnknownProductRow) => sku || <span style={{ color: '#999' }}>-</span>
      },
      {
        nameKey: 'models.unknown_product.name',
        persist: true,
        sortable: true,
        sortFieldName: 'unknown_products.name',
        width: '200px',
        wrap: true,
        cell: ({ name }: UnknownProductRow) => name
      },
      // Same SKU+name on different shops or different (item_id, model_id) now
      // produce distinct rows — show shop + platform so admins can distinguish.
      {
        nameKey: 'models.unknown_product.platform',
        persist: true,
        sortable: false,
        width: '110px',
        cell: ({ platform }: UnknownProductRow) => platform || <span style={{ color: '#999' }}>-</span>
      },
      {
        nameKey: 'models.unknown_product.ec_account',
        persist: true,
        sortable: false,
        width: '160px',
        wrap: true,
        cell: ({ ec_account_name }: UnknownProductRow) => ec_account_name || <span style={{ color: '#999' }}>-</span>
      },
      {
        name: (
          <Space size={4}>
            {t('models.unknown_product.pending_orders_count')}
            <Tooltip title={t('shared.unknown_products.list.column_tooltips.pending_orders_count')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        persist: true,
        sortable: false,
        width: '120px',
        align: 'center',
        cell: ({ pending_orders_count }: UnknownProductRow) => (
          <Tag color={(pending_orders_count ?? 0) > 0 ? 'error' : 'default'}>
            {pending_orders_count}
          </Tag>
        )
      },
      {
        name: (
          <Space size={4}>
            {t('models.unknown_product.total_quantity')}
            <Tooltip title={t('shared.unknown_products.list.column_tooltips.total_quantity')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        persist: true,
        sortable: false,
        width: '100px',
        align: 'right',
        cell: ({ total_quantity }: UnknownProductRow) => (
          <span style={{ fontFamily: 'monospace' }}>{total_quantity}</span>
        )
      },
      {
        nameKey: 'models.unknown_product.created_at',
        persist: true,
        sortable: true,
        sortFieldName: 'unknown_products.created_at',
        width: '160px',
        cell: ({ created_at }: UnknownProductRow) => formatDateTime(created_at as string)
      }
    ]

    // Operator 需要顯示 Merchant 欄位
    if (showMerchantColumn) {
      cols.splice(1, 0, {
        nameKey: 'models.unknown_product.merchant',
        persist: true,
        sortable: true,
        sortFieldName: 'merchants.name',
        width: '150px',
        cell: ({ merchant }: UnknownProductRow) => merchant?.name || '-'
      })
    }

    return cols
  }, [role, showMerchantColumn, t])

  const supportRules = useMemo<SupportRule[]>(() => [
    {
      label: t('models.unknown_product.sku'),
      component: MultipleValue,
      inputName: 'unknown_products.sku',
      defaultPinned: true,
      createable: true,
      tooltip: {
        title: t('shared.unknown_products.list.filter_tooltips.sku.title'),
        content: t('shared.unknown_products.list.filter_tooltips.sku.content'),
      }
    },
    {
      label: t('models.unknown_product.name'),
      component: MultipleValue,
      inputName: 'unknown_products.name',
      createable: true,
      tooltip: {
        title: t('shared.unknown_products.list.filter_tooltips.name.title'),
        content: t('shared.unknown_products.list.filter_tooltips.name.content'),
      }
    }
  ], [t])

  const searchFields = useMemo(() => {
    const fields = [t('models.unknown_product.sku'), t('models.unknown_product.name')]
    // Operator 後端額外以 EXISTS 子查詢搜尋貨主名稱
    if (role === 'operator') {
      fields.push(t('models.merchant.name'))
    }
    return fields
  }, [t, role])

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'prefix',
        searchFields,
      }}
      columnsEditable={true}
      tableId={`${role === 'admin' ? 'Admin' : 'Operator'}UnknownProductsTable`}
      defaultQuery={{
        sorting_field: 'unknown_products.id',
        sorting_direction: 'desc'
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      service={service}
      filterHelpExample={t('shared.unknown_products.list.filter_help_example') as string}
      filterDocsUrl={`/docs/${role}/unknown_products/list#features`}
    />
  )
}
